import React, { useState, useMemo } from 'react';
import SecondaryButton from '../../button/SecondaryButton';
import PrimaryButton from '../../button/PrimaryButton';
import './DataTable.css';

/**
 * DataTable Component
 * Standardized data table with search, sorting, and pagination
 * 
 * @param {Array} data - Array of data objects
 * @param {Array} columns - Array of column definitions {key: string, header: string, render?: function, sortable?: boolean, width?: string}
 * @param {function} onRowClick - Callback when row is clicked: (row: object) => void
 * @param {function} onActionClick - Callback for action button click
 * @param {string} actionLabel - Label for action button
 * @param {string} emptyMessage - Message when no data
 * @param {string} emptyDescription - Description when no data
 * @param {boolean} searchable - Enable search functionality (default: true)
 * @param {boolean} sortable - Enable sorting (default: true)
 * @param {boolean} paginated - Enable pagination (default: true)
 * @param {number} defaultPageSize - Default page size (default: 10)
 * @param {number} minRows - Minimum rows to display (default: 5)
 * @param {string} className - Additional CSS classes
 */
const DataTable = ({
  data = [],
  columns = [],
  onRowClick,
  onActionClick,
  actionLabel,
  emptyMessage = 'No records found',
  emptyDescription = 'No data available.',
  searchable = true,
  sortable = true,
  paginated = true,
  defaultPageSize = 10,
  minRows = 5,
  className = ''
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Search filtering
  const filteredData = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return data;
    }

    const query = searchQuery.toLowerCase();
    return data.filter(row => {
      return columns.some(col => {
        const value = col.render ? col.render(row) : row[col.key];
        return String(value).toLowerCase().includes(query);
      });
    });
  }, [data, searchQuery, searchable, columns]);

  // Sorting
  const sortedData = useMemo(() => {
    if (!sortable || !sortConfig.key) {
      return filteredData;
    }

    const sorted = [...filteredData].sort((a, b) => {
      const col = columns.find(c => c.key === sortConfig.key);
      if (!col) return 0;

      const aValue = col.render ? col.render(a) : a[col.key];
      const bValue = col.render ? col.render(b) : b[col.key];

      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredData, sortConfig, sortable, columns]);

  // Pagination
  const totalRecords = sortedData.length;
  const totalPages = paginated ? Math.ceil(totalRecords / pageSize) : 1;
  const startIndex = paginated ? (currentPage - 1) * pageSize : 0;
  const endIndex = paginated ? startIndex + pageSize : sortedData.length;
  const currentData = sortedData.slice(startIndex, endIndex);

  // Ensure minimum rows for display
  const displayRows = [...currentData];
  while (paginated && displayRows.length < minRows && displayRows.length < totalRecords) {
    displayRows.push({ isEmpty: true, id: `empty-${displayRows.length}` });
  }

  // Handlers
  const handleSort = (key) => {
    if (!sortable) return;

    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(Number(newSize));
    setCurrentPage(1);
  };

  // Get visible page numbers
  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className={`data-table-container ${className}`.trim()}>
      {/* Header with Search and Actions */}
      <div className="data-table-header">
        {searchable && (
          <div className="data-table-search">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="data-table-search-input"
            />
          </div>
        )}
        {actionLabel && onActionClick && (
          <div className="data-table-actions">
            <PrimaryButton onClick={onActionClick} size="small">
              {actionLabel}
            </PrimaryButton>
          </div>
        )}
      </div>

      {/* Table */}
      {sortedData.length > 0 ? (
        <>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col, idx) => (
                    <th
                      key={idx}
                      style={{ width: col.width }}
                      className={sortable && col.sortable !== false ? 'sortable' : ''}
                      onClick={() => sortable && col.sortable !== false && handleSort(col.key)}
                    >
                      <div className="th-content">
                        <span>{col.header}</span>
                        {sortable && col.sortable !== false && sortConfig.key === col.key && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, rowIdx) => (
                  <tr
                    key={row.id || rowIdx}
                    className={`${row.isEmpty ? 'empty-row' : 'data-row'} ${onRowClick && !row.isEmpty ? 'clickable' : ''}`}
                    onClick={() => !row.isEmpty && onRowClick && onRowClick(row)}
                  >
                    {columns.map((col, colIdx) => (
                      <td key={colIdx}>
                        {row.isEmpty ? '' : col.render ? col.render(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {paginated && totalPages > 1 && (
            <div className="data-table-pagination">
              <div className="pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, totalRecords)} of {totalRecords} records
              </div>
              <div className="pagination-controls">
                <div className="page-size-selector">
                  <label>Rows per page:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                    className="page-size-select"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <div className="page-buttons">
                  <SecondaryButton
                    variant="outline"
                    size="small"
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                  >
                    ««
                  </SecondaryButton>
                  <SecondaryButton
                    variant="outline"
                    size="small"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </SecondaryButton>
                  {getVisiblePages().map((page, idx) => {
                    if (page === '...') {
                      return (
                        <span key={`ellipsis-${idx}`} className="page-ellipsis">
                          ...
                        </span>
                      );
                    }
                    return (
                      <SecondaryButton
                        key={page}
                        variant={currentPage === page ? 'primary' : 'outline'}
                        size="small"
                        onClick={() => goToPage(page)}
                        className={currentPage === page ? 'active' : ''}
                      >
                        {page}
                      </SecondaryButton>
                    );
                  })}
                  <SecondaryButton
                    variant="outline"
                    size="small"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </SecondaryButton>
                  <SecondaryButton
                    variant="outline"
                    size="small"
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    »»
                  </SecondaryButton>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="data-table-empty">
          <div className="empty-message">
            <h3>{emptyMessage}</h3>
            <p>{emptyDescription}</p>
            {actionLabel && onActionClick && (
              <PrimaryButton onClick={onActionClick}>
                {actionLabel}
              </PrimaryButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;










